import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateReportDependents } from "./report-cache";
import {
	AI_REPORT_POLL_INTERVAL_MS,
	type AiReportStatus,
	type AiReportWritableStatus,
	shouldPollReports,
} from "./report-status";

export * from "./report-status";
export {
	invalidateReportDependents,
	reportDependentInvalidationKeys,
} from "./report-cache";

// AI Report interfaces
export interface AiReport {
	id: string;
	propertyId: string;
	domain: string;
	/**
	 * Seven values, not five — `processing_discovery` (the discovery fan-out,
	 * the longest phase of a run) and `cancelled` were missing from this union
	 * for as long as it existed, which is why the grid stopped watching a report
	 * exactly when it had the furthest to go. See ./report-status.
	 */
	status: AiReportStatus;
	publicToken: string;
	startedAt: Date;
	completedAt?: Date | null;
	consistencyScore?: number | null;
	// Frozen report-section config (see @repo/core ReportSectionConfig)
	showTechnicalAudit?: boolean;
	showGroundingSearches?: boolean;
	showBrandConsistency?: boolean;
	snapshotId?: string | null;
	visibilitySummary?: any;
	promptsUsed?: any;
	modelsUsed?: any;
	purchaseId?: string | null;
	notes?: string | null;
	createdAt: Date;
	updatedAt: Date;
	// Error message (set when status is "failed")
	errorMessage?: string | null;
	// Progress tracking fields
	progressStep?: string | null;
	progressPercent?: number | null;
	progressMessage?: string | null;
	// Latest discovery metric (Mentions / Share-of-voice list columns).
	// Empty for pending/failed reports.
	discoveryMetrics?: Array<{ totalMentions: number; shareOfVoice: number }>;
	property?: {
		id: string;
		// Null for PERSON/CATEGORY subjects — name the property with
		// getPropertyLabel rather than interpolating this directly.
		domain: string | null;
		companyName?: string | null;
		organizationId: string;
	};
}

export interface CreateAiReportInput {
	propertyId: string;
	domain?: string;
	skipBrandAnalysis?: boolean;
	/**
	 * Narrower than `AiReport["status"]` on purpose: the API validates writes
	 * with `aiReportStatusSchema` (@repo/core), which still only knows five of
	 * the seven statuses the generator writes. Widening this would not
	 * type-check against the Hono RPC client.
	 */
	status?: AiReportWritableStatus;
	startedAt?: Date;
	visibilitySummary?: any;
	promptsUsed?: any;
	modelsUsed?: any;
	// Optional location (uses property default if not provided)
	locationCity?: string | null;
	locationRegion?: string | null;
	locationCountry?: string | null;
	// User confirmed the overage warning (required when the report cost exceeds
	// the org's credit balance — the API returns 409 otherwise)
	acknowledgeOverage?: boolean;
}

export interface UpdateAiReportInput {
	id: string;
	/** See the note on `CreateAiReportInput["status"]`. */
	status?: AiReportWritableStatus;
	completedAt?: Date | null;
	consistencyScore?: number | null;
	snapshotId?: string | null;
	visibilitySummary?: any;
	promptsUsed?: any;
	modelsUsed?: any;
	purchaseId?: string | null;
	notes?: string | null;
}

// Query keys
export const aiReportListQueryKey = (
	organizationId?: string,
	propertyId?: string,
) => {
	if (propertyId) {
		return ["ai-reports", "property", propertyId] as const;
	}
	if (organizationId) {
		return ["ai-reports", "organization", organizationId] as const;
	}
	return ["ai-reports"] as const;
};

export const aiReportQueryKey = (id: string) => ["ai-reports", id] as const;

// Hooks
export const useAiReportsQuery = (
	organizationId?: string,
	propertyId?: string,
) =>
	useQuery({
		queryKey: aiReportListQueryKey(organizationId, propertyId),
		queryFn: async () => {
			const query: any = {};
			if (organizationId) query.organizationId = organizationId;
			if (propertyId) query.propertyId = propertyId;

			const response = await apiClient["ai-reports"].$get({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch AI reports");
			}

			const data = (await response.json()) as any[];
			// Convert string dates to Date objects
			return data.map((item: any) => ({
				...item,
				startedAt: new Date(item.startedAt),
				completedAt: item.completedAt
					? new Date(item.completedAt)
					: null,
				createdAt: new Date(item.createdAt),
				updatedAt: new Date(item.updatedAt),
			})) as AiReport[];
		},
		enabled: !!(organizationId || propertyId),
		/**
		 * Self-converging list: poll only while at least one listed report is
		 * still moving, and stop the moment they are all terminal. This replaces a
		 * `setInterval` in AiReportsManager whose in-flight test left out
		 * `processing_discovery`, so a report in its longest phase never refreshed
		 * and the row sat on "Processing Discovery Queries" until the 30s
		 * staleTime plus a window-focus refetch happened to clear it.
		 *
		 * `refetchIntervalInBackground` is left at its default (false), so a
		 * hidden tab costs nothing.
		 */
		refetchInterval: (query) =>
			shouldPollReports(query.state.data as AiReport[] | undefined)
				? AI_REPORT_POLL_INTERVAL_MS
				: false,
	});

export const useAiReportQuery = (id: string) =>
	useQuery({
		queryKey: aiReportQueryKey(id),
		queryFn: async () => {
			const response = await apiClient["ai-reports"][":id"].$get({
				param: { id },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch AI report");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				startedAt: new Date(data.startedAt),
				completedAt: data.completedAt
					? new Date(data.completedAt)
					: null,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
			} as AiReport;
		},
	});

export const useCreateAiReportMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateAiReportInput) => {
			const response = await apiClient["ai-reports"].$post({
				json: input,
			});

			if (!response.ok) {
				throw new Error("Failed to create AI report");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				startedAt: new Date(data.startedAt),
				completedAt: data.completedAt
					? new Date(data.completedAt)
					: null,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
			} as AiReport;
		},
		onSuccess: (data) => {
			// A queued report already satisfies "has a report" for onboarding and
			// for the workflow companion. The helper invalidates the whole
			// `ai-reports` prefix, so both the property list and the organization
			// list are covered.
			invalidateReportDependents(queryClient, data.propertyId);
		},
	});
};

export const useUpdateAiReportMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: UpdateAiReportInput) => {
			const response = await apiClient["ai-reports"][":id"].$patch({
				param: { id: input.id },
				json: input,
			});

			if (!response.ok) {
				throw new Error("Failed to update AI report");
			}

			const data = (await response.json()) as any;
			// Convert string dates to Date objects
			return {
				...data,
				startedAt: new Date(data.startedAt),
				completedAt: data.completedAt
					? new Date(data.completedAt)
					: null,
				createdAt: new Date(data.createdAt),
				updatedAt: new Date(data.updatedAt),
			} as AiReport;
		},
		onSuccess: (data) => {
			// Invalidate and refetch both individual report and lists
			queryClient.invalidateQueries({
				queryKey: aiReportQueryKey(data.id),
			});
			queryClient.invalidateQueries({
				queryKey: aiReportListQueryKey(
					data.property?.organizationId,
					data.propertyId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: aiReportListQueryKey(data.property?.organizationId),
			});
		},
	});
};

/**
 * `propertyId` is optional only because the endpoint takes a bare report id.
 * Pass it where it is known: deleting the last report flips `hasAiReports`
 * back off, which the onboarding strip and the brand dashboard's baseline
 * gate both read.
 */
export const useDeleteAiReportMutation = (propertyId?: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient["ai-reports"][":id"].$delete({
				param: { id },
			});

			if (!response.ok) {
				throw new Error("Failed to delete AI report");
			}
		},
		onSuccess: (_, id) => {
			// Remove from cache and invalidate lists
			queryClient.removeQueries({ queryKey: aiReportQueryKey(id) });
			invalidateReportDependents(queryClient, propertyId);
		},
	});
};

// Credit-based unlock
export interface UnlockOptions {
	canUnlockWithCredits: boolean;
	canUnlockWithLegacyPlan: boolean;
	canPurchaseOneTime: boolean;
	creditsRequired?: number;
	creditsAvailable?: number;
	isAlreadyUnlocked: boolean;
	belongsToOrganization: boolean;
	organizationId?: string; // Backend-determined organization ID
}

export const unlockOptionsQueryKey = (token: string, organizationId?: string) =>
	["ai-reports", "unlock-options", token, organizationId] as const;

export const useUnlockOptions = (token: string, organizationId?: string) =>
	useQuery({
		queryKey: unlockOptionsQueryKey(token, organizationId),
		queryFn: async () => {
			const response = await apiClient["public-ai-reports"][
				"unlock-options"
			].$get({
				query: {
					token,
					...(organizationId ? { organizationId } : {}),
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch unlock options");
			}

			return (await response.json()) as UnlockOptions;
		},
		enabled: !!token,
	});

export const useUnlockWithCreditsMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			reportPublicToken,
			organizationId,
		}: {
			reportPublicToken: string;
			organizationId: string;
		}) => {
			const response = await apiClient["ai-reports"][
				"unlock-with-credits"
			].$post({
				json: {
					reportPublicToken,
					organizationId,
				},
			});

			if (!response.ok) {
				const errorData = await response.json();
				const errorMessage =
					errorData &&
					typeof errorData === "object" &&
					"error" in errorData
						? errorData.error
						: "Failed to unlock report";
				throw new Error(errorMessage as string);
			}

			return await response.json();
		},
		onSuccess: (_, { reportPublicToken, organizationId }) => {
			// Invalidate unlock options
			queryClient.invalidateQueries({
				queryKey: unlockOptionsQueryKey(
					reportPublicToken,
					organizationId,
				),
			});
			// Invalidate AI reports lists
			queryClient.invalidateQueries({ queryKey: ["ai-reports"] });
			// Invalidate purchase status (for UnlockStatus component)
			queryClient.invalidateQueries({
				queryKey: ["ai-report", "purchase-status", reportPublicToken],
			});
		},
	});
};

// Set email notification for report completion
export const useSetReportNotifyEmailMutation = () => {
	return useMutation({
		mutationFn: async ({
			publicToken,
			email,
		}: {
			publicToken: string;
			email: string;
		}) => {
			const response = await apiClient["public-ai-reports"][
				"notify-email"
			].$post({
				json: {
					publicToken,
					email,
				},
			});

			if (!response.ok) {
				const errorData = await response.json();
				const errorMessage =
					errorData &&
					typeof errorData === "object" &&
					"error" in errorData
						? errorData.error
						: "Failed to set notification";
				throw new Error(errorMessage as string);
			}

			return await response.json();
		},
	});
};
